import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Pencil,
  Search,
  Funnel,
  Trash2,
  Edit2,
  Plus,
  MoreHorizontal,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  Copy
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import apiHelper from '../../apiHelper';
import { copyToClipboard } from '../../utils/helper';
import { useLoader } from '../../components/GlobalLoader';

const PAGE_SIZE = 8;

const Company = () => {
  const history = useHistory();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const [entities, setEntities] = useState([]);
  const [organisation, setOrganisation] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);

  const handleToggleStatus = async (row) => {
    const activate = row.status_text !== 'Active';
    const action = activate ? 'activate' : 'deactivate';
    const updateData = { status: action };
    const response = await apiHelper(`entities/update-status/${row.entity_id}/`, 'POST', updateData);
    if (response && response.ok) {
      setEntities(entities.map(e =>
        e.entity_id === row.entity_id
          ? { ...e, status_text: activate ? 'Active' : 'Inactive' }
          : e
      ));
      setDropdownOpenId(null);
    }
  };

  const [activeTab, setActiveTab] = useState(() => location.state?.tab || "organisation-info");

  const formatTab = (tab) =>
    tab.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const breadcrumbs = [
    "Organisations",
    "Company",
    formatTab(activeTab)
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [entitySearchStatus, setEntitySearchStatus] = useState([]);
  const [entitySearchParent, setEntitySearchParent] = useState([]);
  const [entityParentSearchTerm, setEntityParentSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('entity-info');
  const [selectedRow, setSelectedRow] = useState(null);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return entities.filter(d => {
      const matchesSearch =
        !searchTerm ||
        d.entity_name.toLowerCase().includes(term) ||
        d.uen.toLowerCase().includes(term) ||
        d.note.toLowerCase().includes(term) ||
        d.entity_short_name.toLowerCase().includes(term) ||
        d.uuid.toLowerCase().includes(term);
      const matchesStatus = entitySearchStatus.length === 0 || entitySearchStatus.includes(d.status_text);
      const matchesParent = entitySearchParent.length === 0 || entitySearchParent.includes(d.parent_name || 'NA');
      return matchesSearch && matchesStatus && matchesParent;
    });
  }, [searchTerm, entities, entitySearchStatus, entitySearchParent]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleSelect = id =>
    setSelectedIds(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );

  const toggleAll = () => {
    if (selectedIds.length === pageData.length) setSelectedIds([]);
    else setSelectedIds(pageData.map(d => d.entity_id));
  };

  const onRowClick = row => {
    setSelectedRow(row);
    setDrawerTab('entity-info');
    setDrawerOpen(true);
  };

  const onWpRowClick = row => {
    setSelectedWorkspace(row);
    setWpDrawerTab('workplace-info');
    setWpDrawerOpen(true);
  };

  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});
  const drawerRef = useRef(null);

  const [wpDrawerOpen, setWpDrawerOpen] = useState(false);
  const [wpDrawerTab, setWpDrawerTab] = useState('workplace-info');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const wpDrawerRef = useRef(null);

  const currentRow = entities.find(e => e.entity_id === dropdownOpenId);

  const editEntry = () => {
    if (selectedIds.length == 1) {
      history.push(`/company/edit-entities/${selectedIds[0]}`);
    }
  };

  const deleteEntry = async () => {
    if (selectedIds.length) {
      let body = { 'entities': selectedIds };
      if (window.confirm("Are you sure want to delete?")) {
        const response = await apiHelper('entities/delete/', 'POST', body);
        if (response.ok) {
          const new_entities = entities.filter(entity => !selectedIds.includes(entity.entity_id));
          setEntities(new_entities);
          setSelectedIds([]);
          alert("Entity Successfully Deleted");
        }
      }
    }
  };
  console.log("location state", location.state);
useEffect(() => {
  if (location.state?.activeTab) {
    setActiveTab(location.state.activeTab);
  }
}, [location.state]);
  const deleteWorkplace = async () => {
    if (!wpSelectedIds.length) return;
    if (!window.confirm("Are you sure want to delete?")) return;
    const res = await apiHelper('entities/workplaces-delete/', 'POST', { workplaces: wpSelectedIds });
    if (res.ok) {
      setWorkplaces(workplaces.filter(w => !wpSelectedIds.includes(w.workplace_id)));
      setWpSelectedIds([]);
      alert("Workplace Successfully Deleted");
    }
  };

  const editWorkplace = () => {
    if (wpSelectedIds.length == 1) {
      history.push(`/company/edit-workplaces/${wpSelectedIds[0]}`);
    }
  };

  useEffect(() => {
    async function fetchOrganisation() {
      const response = await apiHelper('entities/organisation/');
      if (response && response.ok) {
        const result = await response.json();
        setOrganisation(result);
      }
    }

    async function fetchWorkplaces() {
      const response = await apiHelper('entities/workplaces/');
      if (response && response.ok) {
        const result = await response.json();
        setWorkplaces(result);
      }
    }

    async function fetchData() {
      const response = await apiHelper('entities/list/');
      if (response && response.ok) {
        const result = await response.json();
        setEntities(result);
      }
    }

    const loadAll = async () => {
      showLoader();
      try {
        await Promise.all([fetchOrganisation(), fetchData(), fetchWorkplaces()]);
      } catch (error) {
        console.error('Failed to load company data:', error);
      } finally {
        hideLoader();
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    function handleClickOutsideDrawer(event) {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
        setSelectedRow(null);
        setSelectedIds(ids =>
          selectedRow ? ids.filter(id => id !== selectedRow.entity_id) : ids
        );
      }
    }
    document.addEventListener('mousedown', handleClickOutsideDrawer);
    return () =>
      document.removeEventListener('mousedown', handleClickOutsideDrawer);
  }, [drawerOpen, selectedRow]);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRefs.current[dropdownOpenId]?.contains(event.target)
      ) {
        setDropdownOpenId(null);
      }
    }

    if (dropdownOpenId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpenId]);

  const toggleDropdown = (id) => {
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
    } else {
      const btn = buttonRefs.current[id];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const dropdownWidth = 150;
        const dropdownHeight = 100;
        let left = rect.left + window.scrollX - dropdownWidth + rect.width;
        if (left < window.scrollX + 8) {
          left = window.scrollX + 8;
        }
        let top = rect.bottom + window.scrollY;
        if (top + dropdownHeight > window.scrollY + window.innerHeight) {
          top = rect.top + window.scrollY - dropdownHeight;
        }
        setDropdownPosition({ top, left });
      }
      setDropdownOpenId(id);
    }
  };

  const [wpSearchTerm, setWpSearchTerm] = useState('');
  const [wpSearchStatus, setWpSearchStatus] = useState([]);
  const [wpSearchLocationType, setWpSearchLocationType] = useState([]);
  const [wpSearchAddress, setWpSearchAddress] = useState([]);
  const [wpAddressSearchTerm, setWpAddressSearchTerm] = useState('');
  const [wpCurrentPage, setWpCurrentPage] = useState(1);
  const [wpSelectedIds, setWpSelectedIds] = useState([]);

  const filteredWorkplaces = useMemo(() => {
    const term = wpSearchTerm.toLowerCase();
    return workplaces.filter(w => {
      const address = [w.street_address, w.street_address_2, w.city, w.state, w.post_code, w.country]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch =
        !wpSearchTerm ||
        (w.workplace_name ? w.workplace_name.toLowerCase().includes(term) : false) ||
        (w.uuid ? w.uuid.toLowerCase().includes(term) : false) ||
        (w.workplace_short_name ? w.workplace_short_name.toLowerCase().includes(term) : false) ||
        (w.notes ? w.notes.toLowerCase().includes(term) : false) ||
        address.includes(term);
      const matchesStatus = wpSearchStatus.length === 0 || wpSearchStatus.includes(w.status_text);
      const matchesLocationType = wpSearchLocationType.length === 0 || wpSearchLocationType.includes(w.location_type);
      const matchesAddress = wpSearchAddress.length === 0 || wpSearchAddress.some(addr => address.includes(addr.toLowerCase()));
      return matchesSearch && matchesStatus && matchesLocationType && matchesAddress;
    });
  }, [wpSearchTerm, workplaces, wpSearchStatus, wpSearchLocationType, wpSearchAddress]);

  const wpTotalPages = Math.ceil(filteredWorkplaces.length / PAGE_SIZE);
  const wpPageData = filteredWorkplaces.slice((wpCurrentPage - 1) * PAGE_SIZE, wpCurrentPage * PAGE_SIZE);

  const toggleSelectWp = id =>
    setWpSelectedIds(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );

  const toggleAllWp = () => {
    if (wpSelectedIds.length === wpPageData.length) setWpSelectedIds([]);
    else setWpSelectedIds(wpPageData.map(d => d.workplace_id));
  };

  const [wpDropdownOpenId, setWpDropdownOpenId] = useState(null);
  const wpButtonRefs = useRef({});
  const [wpDropdownPosition, setWpDropdownPosition] = useState({ top: 0, left: 0 });

  const toggleWpDropdown = id => {
    if (wpDropdownOpenId === id) return setWpDropdownOpenId(null);
    const btn = wpButtonRefs.current[id];
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    let left = rect.left + window.scrollX - 150 + rect.width;
    if (left < 8) left = 8;
    let top = rect.bottom + window.scrollY;
    if (top + 100 > window.scrollY + window.innerHeight)
      top = rect.top + window.scrollY - 100;
    setWpDropdownPosition({ top, left });
    setWpDropdownOpenId(id);
  };

  const wpCurrentRow = workplaces.find(e => e.workplace_id === wpDropdownOpenId);

  const handleWpToggleStatus = async (row) => {
    const activate = row.status_text !== 'Active';
    const action = activate ? 'activate' : 'deactivate';
    setActivateFailedMessage('');
    const response = await apiHelper(`entities/workplaces-update-status/${row.workplace_id}/`, 'POST', { status: action });
    if (response.ok) {
      setWorkplaces(workplaces.map(w =>
        w.workplace_id === row.workplace_id
          ? { ...w, status_text: activate ? 'Active' : 'Inactive' }
          : w
      ));
      setWpDropdownOpenId(null);
    } else if (response.status === 400) {
      const response_errors = await response.json();
      setActivateFailedMessage(response_errors.detail);
    }
  };

  const filterButtonRef = useRef(null);
  const filterPopupRef = useRef(null);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [copied, setCopied] = useState(false);
  const wpFilterButtonRef = useRef(null);
  const wpFilterPopupRef = useRef(null);
  const [wpFilterPopupOpen, setWpFilterPopupOpen] = useState(false);
  const [wpFilterPosition, setWpFilterPosition] = useState({ top: 0, left: 0 });
  const [hasAutoOpened, setHasAutoOpened] = useState(false);


  const handleCheckboxChange = (setter, value) => (e) => {
    const isChecked = e.target.checked;
    setter(prev =>
      isChecked
        ? [...prev, value]
        : prev.filter(v => v !== value)
    );
    setCurrentPage(1);
  };

  const handleWpCheckboxChange = (setter, value) => (e) => {
    const isChecked = e.target.checked;
    setter(prev =>
      isChecked
        ? [...prev, value]
        : prev.filter(v => v !== value)
    );
    setWpCurrentPage(1);
  };

  useEffect(() => {
    if (filterPopupOpen && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      setFilterPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [filterPopupOpen]);

  useEffect(() => {
    if (wpFilterPopupOpen && wpFilterButtonRef.current) {
      const rect = wpFilterButtonRef.current.getBoundingClientRect();
      setWpFilterPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [wpFilterPopupOpen]);

  useEffect(() => {
    function handleClickOutsideFilter(event) {
      if (
        filterPopupOpen &&
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setFilterPopupOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideFilter);
    return () => document.removeEventListener('mousedown', handleClickOutsideFilter);
  }, [filterPopupOpen]);

  useEffect(() => {
    function handleClickOutsideWpFilter(event) {
      if (
        wpFilterPopupOpen &&
        wpFilterPopupRef.current &&
        !wpFilterPopupRef.current.contains(event.target) &&
        !wpFilterButtonRef.current.contains(event.target)
      ) {
        setWpFilterPopupOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideWpFilter);
    return () => document.removeEventListener('mousedown', handleClickOutsideWpFilter);
  }, [wpFilterPopupOpen]);

  const entityFilterCount = [...entitySearchStatus, ...entitySearchParent].filter(Boolean).length;
  const wpFilterCount = [...wpSearchStatus, ...wpSearchLocationType, ...wpSearchAddress].filter(Boolean).length;


  const handleCopy = async (textToCopy) => {
    if (!textToCopy) {
      console.error('No text to copy');
      alert('Failed to copy: No text available.');
      return;
    }

    // Use Clipboard API if available and secure
    if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
      } catch (err) {
        console.error('Clipboard API failed:', err);
        fallbackCopyTextToClipboard(textToCopy);
      }
    } else {
      console.warn('Clipboard API not available or not in secure context, using fallback');
      fallbackCopyTextToClipboard(textToCopy);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Fallback copy failed');
        alert('Failed to copy text. Please copy manually.');
      }
    } catch (err) {
      console.error('Fallback copy error:', err);
      alert('Failed to copy text. Please copy manually.');
    }
  };
  useEffect(() => {
  const query = new URLSearchParams(location.search);
  const autoOpen = query.get("autoOpen");
  const id = query.get("id");
  const tab = query.get("tab");

  if (autoOpen === "true" && tab === "workplaces" && id && wpPageData.length > 0) {
    setActiveTab(tab);
    const row = wpPageData.find(wp => String(wp.workplace_id) === id);
    if (row) {
      onWpRowClick(row); // 👈 this simulates clicking the row → opens drawer
    }
  }
}, [location.search, wpPageData]);
const [successMessage, setSuccessMessage] = useState("");
const [activateFailedMessage, setActivateFailedMessage] = useState("");


  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.success);

      // clear after 4s
      setTimeout(() => {
        setSuccessMessage("");
      }, 4000);
    }
  }, [location.state]);

  
  return (
    <div className="company-body">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="breadcrumb-item">
            {crumb}
            {index !== breadcrumbs.length - 1}
          </span>
        ))}
      </div>
      
                      {successMessage && (
          <div
              className="d-flex align-items-center justify-content-between"
              role="alert"
              style={{
                  boxShadow: "0px 4px 10px 0px #0000001F",
                  padding: "15px 10px",
                  borderRadius: "12px",
                  border: "1px solid #28a745",   // green border
                  backgroundColor: "#d4edda",    // light green background
                  color: "#155724",              // dark green text
                  fontSize: "12px",
                  position: "fixed",
                  top: "10px",
                  width: "calc(100% - 20px)",
                  maxWidth: "640px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 2000,
              }}
          >
              <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                  <strong>{successMessage}</strong>
              </div>
      
              <X
                  size={16}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSuccessMessage("")}
              />
          </div>
      )}

{activateFailedMessage && (
                <div
                    className="d-flex align-items-center justify-content-between"
                    role="alert"
                    style={{
                        boxShadow: "0px 4px 10px 0px #0000001F",
                        padding: "15px 10px",
                        borderRadius: "12px",
                        border: "1px solid #DC3545",
                        backgroundColor: "#F8D7DA",
                        color: "#842029",
                        fontSize: "12px",
                        position: "fixed",
                        top: "10px",
                        width: "calc(100% - 20px)",
                        maxWidth: "640px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2000,
                    }}
                >
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                        <strong>{activateFailedMessage}</strong>
                    </div>

                    <X
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => setActivateFailedMessage('')}
                    />
                </div>
            )}


      <div className="tabs">
        {["organisation-info", "entities", "workplaces"].map((tab) => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {formatTab(tab)}
          </button>
        ))}
      </div>

      {activeTab === "organisation-info" && (
        <div className="info-section">
          <div className="info-card">
            <div className="info-header">
              <h4 className="info-title">Organisation Name</h4>
            </div>
            <div className="info-content">
              <div><strong>{organisation.organisation_name}</strong></div>
              <button className="edit-btn" onClick={() => history.push("/company/edit-organisation")}>
                <Pencil size={24} />
                Edit Info
              </button>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <h4 className="info-title">Basic Information</h4>
            </div>
            <div className="info-content">
              <div>
                <strong className="mb-2 notes-preserve">Notes</strong>
                <p>{organisation.notes}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "entities" && (
        <div className="info-section entities-cls-adding-here">
          <div className="info-card">
            <div className="info-header">
              <h4 className="info-title">Entities</h4>
            </div>

            <div className="entities-tab">
              <div className="toolbar">
                <div className="toolbar-left">
                  <div className="search-box">
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="Search…"
                      value={searchTerm}
                      onChange={e => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <button
                    ref={filterButtonRef}
                    className="icon-btn new-look"
                    onClick={() => setFilterPopupOpen(!filterPopupOpen)}
                  >
                    <Funnel size={18} /> Filter
                    {entityFilterCount > 0 && (
                      <span
                        style={{
                          background: '#000',
                          color: '#ffffff',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          marginLeft: '5px'
                        }}
                      >
                        {entityFilterCount}
                      </span>
                    )}
                  </button>
                </div>
                <div className="toolbar-right">
                  <button className="icon-btn" onClick={deleteEntry}><Trash2 size={18} /></button>
                  <button className="icon-btn" onClick={editEntry}><Edit2 size={18} /></button>
                  <button className="icon-btn" onClick={() => history.push("/company/add-entities")}><Plus size={18} /></button>
                </div>
              </div>

              {filterPopupOpen && (
                <div
                  ref={filterPopupRef}
                  className="filter-popup"
                  style={{
                    position: 'absolute',
                    top: filterPosition.top,
                    left: filterPosition.left,
                    background: '#fff',
                    padding: '16px',
                    borderRadius: '8px',
                    width: '300px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    zIndex: '1002',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  <div className="filter-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <button className="close-btn" onClick={() => setFilterPopupOpen(false)}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="filter-content">
                    {/* Status */}
                    <div className="filter-section" style={{ marginBottom: '16px' }}>
                      <label className="form-label">Status</label>
                      <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                        {['Active', 'Inactive'].map(status => (
                          <label key={status} className="form-check" style={{ cursor: 'pointer', fontWeight: 400 }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={entitySearchStatus.includes(status)}
                              onChange={handleCheckboxChange(setEntitySearchStatus, status)}
                            />
                            <span style={{ marginLeft: '8px' }}>{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Parent Entity */}
                    <div className="filter-section" style={{ marginBottom: '16px' }}>
                      <label className="form-label">Parent Entity</label>
                      <div className="search-box-filter" style={{
                        position: 'relative',
                        border: '1px solid #6c757d',
                        borderRadius: '.5rem',
                        padding: '4px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginBottom: '6px'
                      }}>
                        {entitySearchParent.map(parent => (
                          <span
                            key={parent}
                            style={{
                              backgroundColor: '#FFEB00',
                              color: '#212529',
                              borderRadius: '16px',
                              padding: '4px 8px',
                              margin: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              lineHeight: 'normal'
                            }}
                          >
                            {parent}
                            <button
                              onClick={() =>
                                handleCheckboxChange(setEntitySearchParent, parent)({ target: { checked: false } })
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                marginLeft: '20px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '0',
                                lineHeight: '14px'
                              }}
                            >
                              <X size={14} strokeWidth={3} />
                            </button>
                          </span>
                        ))}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '100px' }}>
                          <Search size={16} style={{ marginRight: '4px' }} />
                          <input
                            type="text"
                            placeholder="Search Parent Entity…"
                            value={entityParentSearchTerm}
                            onChange={e => setEntityParentSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
                          />
                        </div>
                      </div>
                      <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                        {[...new Set(
                          entities
                            .filter(d => (d.parent_name || '').toLowerCase().includes(entityParentSearchTerm.toLowerCase()) && d.parent_name !== 'NA')
                            .map(d => d.parent_name)
                        )]
                          .slice(0, 3)
                          .map((parent, index) => (
                            <label key={index} className="form-check" style={{ cursor: 'pointer', fontWeight: 400 }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={entitySearchParent.includes(parent)}
                                onChange={handleCheckboxChange(setEntitySearchParent, parent)}
                              />
                              <span style={{ marginLeft: '8px' }}>{parent}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  </div>

                  <div className="filter-footer" style={{ textAlign: 'center', marginTop: '10px' }}>
                    <button
                      className="filter-btn"
                      onClick={() => setFilterPopupOpen(false)}
                      style={{
                        background: 'black',
                        color: 'white',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Filter
                    </button>
                  </div>
                </div>

              )}

              <div className="table-wrapper">
                <table className="entities-table">
                  <thead>
                    <tr>
                      <th>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedIds.length === pageData.length && pageData.length > 0}
                            onChange={toggleAll}
                          />
                        </div>
                      </th>
                      <th>
                        <span>Entity Name <ChevronsUpDown size={12} /></span>
                      </th>
                      <th>
                        <span>UEN <ChevronsUpDown size={12} /></span>
                      </th>
                      <th>
                        <span>Status <ChevronsUpDown size={12} /></span>
                      </th>
                      <th>
                        <span>Parent Entity <ChevronsUpDown size={12} /></span>
                      </th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, idx) => (
                      <tr
                        key={row.entity_id}
                        className={[
                          idx % 2 ? 'striped' : '',
                          (selectedIds.includes(row.entity_id) || selectedRow?.entity_id === row.entity_id)
                            ? 'row-selected'
                            : ''
                        ].join(' ')}
                        onClick={() => onRowClick(row)}
                      >
                        <td>
                          <div className='form-check' onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedIds.includes(row.entity_id)}
                              onChange={e => {
                                toggleSelect(row.entity_id);
                                if (e.target.checked) {
                                  setSelectedRow(row);
                                } else if (selectedRow?.entity_id === row.entity_id) {
                                  setSelectedRow(null);
                                }
                              }}
                            />
                          </div>
                        </td>
                        <td>{row.entity_name}</td>
                        <td>{row.uen}</td>
                        <td>
                          <span className={`status ${row.status_text.toLowerCase()}`}>
                            {row.status_text}
                          </span>
                        </td>
                        <td>{row.parent_name || 'NA'}</td>
                        <td style={{ textAlign: "center", position: "relative" }}>
                          <button
                            ref={(el) => (buttonRefs.current[row.entity_id] = el)}
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(row.entity_id);
                            }}
                          >
                            <MoreHorizontal size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {dropdownOpenId !== null && (
                  <div
                    ref={dropdownRef}
                    className='backdrop-cls'
                    style={{
                      position: "absolute",
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                      background: "white",
                      boxShadow: "0px 1px 4px 0px #0000001A",
                      zIndex: 1000,
                      minWidth: 152,
                      borderRadius: 8,
                      padding: 12,
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className='btn-align-forall'>
                      <div className='common-for-all'>
                        <button
                          onClick={() => {
                            onRowClick(currentRow);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                        >
                          View Details
                        </button>
                      </div>

                      <div className='common-for-all'>
                        <button
                          onClick={() => {
                            history.push(`/company/edit-entities/${currentRow.entity_id}`);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                        >
                          Edit
                        </button>
                      </div>

                      <div className='common-for-all'>
                        <button
                          onClick={() => {
                            handleToggleStatus(currentRow);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                          className='deactivate-cls'
                        >
                          {currentRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {filtered.length > 0 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} -
                    {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} items
                  </div>
                  <div className="pagination-control">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={currentPage === i + 1 ? 'active' : ''}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {drawerOpen && selectedRow && (
        <>
          <div className="drawer-overlay" />
          <div className="entity-drawer" ref={drawerRef}>
            <div className="drawer-header">
              <h3>Entities Info</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setDrawerOpen(false);
                  setSelectedIds((ids) => ids.filter(id => id !== selectedRow?.entity_id));
                  setSelectedRow(null);
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className='main-drawer-inner'>
              <div className='drawer-title'>
                <h2>{selectedRow.entity_name}</h2>
              </div>
              <div className="drawer-tabs">
                <button
                  className={drawerTab === 'entity-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('entity-info')}
                >
                  Entity Info
                </button>
                <button
                  className={drawerTab === 'additional-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('additional-info')}
                >
                  Additional Info
                </button>
              </div>
              <div className="drawer-content">
                {drawerTab === 'entity-info' ? (
                  <div className="tab-panel entity-info-panel">
                    <div className="field-block">
                      <div className="field-label">Entity Name</div>
                      <div className="field-value">{selectedRow.entity_name}</div>
                    </div>
                    <div className="field-block">
                      <div className="field-label">Open ID</div>
                      <div className="field-value d-flex align-items-center gap-2">
                        <span
                          style={{ color: "#2C7BE5", cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => handleCopy(selectedRow.uuid)}
                        >
                          {selectedRow.uuid}
                        </span>
                        <Copy
                          size={13}
                          onClick={() => handleCopy(selectedRow.uuid)}
                          style={{ cursor: "pointer", color: "#2C7BE5" }}
                        />
                        {copied && <span style={{ fontSize: '12px', color: "#2C7BE5" }}>Copied!</span>}
                      </div>
                    </div>
                    <div className="field-block">
                      <div className="field-label">Status</div>
                      <div className="field-value">
                        <span className={`status ${selectedRow.status_text.toLowerCase()}`}>
                          {selectedRow.status_text}
                        </span>
                      </div>
                    </div>
                    <div className="field-block">
                      <div className="field-label">UEN</div>
                      <div className="field-value">{selectedRow.uen}</div>
                    </div>
                    <div className="field-block">
                      <div className="field-label">Short Name</div>
                      <div className="field-value">{selectedRow.entity_short_name}</div>
                    </div>
                  </div>
                ) : (
                  <div className="tab-panel additional-info-panel">
                    <p className='note-type-added'>Note</p>
                    <label className='no-description-cls notes-preserve'>{selectedRow.note}</label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "workplaces" && (
        <div className="info-section">
          <div className="info-card">
            <div className="info-header">
              <h4 className="info-title">Workplaces</h4>
            </div>
            <div className="entities-tab">
              <div className="toolbar">
                <div className="toolbar-left">
                  <div className="search-box">
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="Search…"
                      value={wpSearchTerm}
                      onChange={e => { setWpSearchTerm(e.target.value); setWpCurrentPage(1); }}
                    />
                  </div>
                  <button
                    ref={wpFilterButtonRef}
                    className="icon-btn new-look"
                    onClick={() => setWpFilterPopupOpen(!wpFilterPopupOpen)}
                  >
                    <Funnel size={18} /> Filter
                    {wpFilterCount > 0 && (
                      <span
                        style={{
                          background: '#000',
                          color: '#ffffff',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          marginLeft: '5px'
                        }}
                      >
                        {wpFilterCount}
                      </span>
                    )}
                  </button>
                </div>
                <div className="toolbar-right">
                  <button className="icon-btn" onClick={deleteWorkplace}><Trash2 size={18} /></button>
                  <button className="icon-btn"><Edit2 onClick={editWorkplace} size={18} /></button>
                  <button className="icon-btn" onClick={() => history.push("/company/add-workplaces")}><Plus size={18} /></button>
                </div>
              </div>

              {wpFilterPopupOpen && (
                <div
                  ref={wpFilterPopupRef}
                  className="filter-popup"
                  style={{
                    position: 'absolute',
                    top: wpFilterPosition.top,
                    left: wpFilterPosition.left,
                    background: '#fff',
                    padding: '16px',
                    borderRadius: '8px',
                    width: '300px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    zIndex: '1002',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  <div className="filter-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <button className="close-btn" onClick={() => setWpFilterPopupOpen(false)}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="filter-content">
                    {/* Status */}
                    <div className="filter-section" style={{ marginBottom: '16px' }}>
                      <label className="form-label">Status</label>
                      <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                        {['Active', 'Inactive'].map(status => (
                          <label key={status} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={wpSearchStatus.includes(status)}
                              onChange={handleWpCheckboxChange(setWpSearchStatus, status)}
                            />
                            <span style={{ marginLeft: '8px' }}>{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Location Type */}
                    <div className="filter-section" style={{ marginBottom: '16px' }}>
                      <label className="form-label">Location Type</label>
                      <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                        {[...new Set(workplaces.map(w => w.location_type))].filter(Boolean).map(type => (
                          <label key={type} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={wpSearchLocationType.includes(type)}
                              onChange={handleWpCheckboxChange(setWpSearchLocationType, type)}
                            />
                            <span style={{ marginLeft: '8px' }}>{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="filter-section">
                      <label className="form-label">Address</label>
                      <div className="search-box-filter" style={{
                        position: 'relative',
                        border: '1px solid #6c757d',
                        borderRadius: '.5rem',
                        padding: '4px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginBottom: '6px'
                      }}>
                        {wpSearchAddress.map(addr => (
                          <span
                            key={addr}
                            style={{
                              backgroundColor: '#FFEB00',
                              color: '#212529',
                              borderRadius: '16px',
                              padding: '4px 8px',
                              margin: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              lineHeight: 'normal'
                            }}
                          >
                            {addr}
                            <button
                              onClick={() =>
                                handleWpCheckboxChange(setWpSearchAddress, addr)({ target: { checked: false } })
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                marginLeft: '20px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '0',
                                lineHeight: '14px'
                              }}
                            >
                              <X size={14} strokeWidth={3} />
                            </button>
                          </span>
                        ))}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '100px' }}>
                          <Search size={16} style={{ marginRight: '4px' }} />
                          <input
                            type="text"
                            placeholder="Search Address…"
                            value={wpAddressSearchTerm}
                            onChange={e => setWpAddressSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
                          />
                        </div>
                      </div>
                      <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                        {[...new Set(
                          workplaces
                            .filter(w => {
                              const address = [w.street_address, w.street_address_2, w.city, w.state, w.post_code, w.country].filter(Boolean).join(' ').toLowerCase();
                              return address.includes(wpAddressSearchTerm.toLowerCase());
                            })
                            .map(w => [w.street_address, w.street_address_2, w.city, w.state, w.post_code, w.country].filter(Boolean).join(' '))
                        )].slice(0, 3).map((addr, index) => (
                          <label key={index} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={wpSearchAddress.includes(addr)}
                              onChange={handleWpCheckboxChange(setWpSearchAddress, addr)}
                            />
                            <span style={{ marginLeft: '8px' }}>{addr}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="filter-footer" style={{ textAlign: 'center', marginTop: '10px' }}>
                    <button
                      className="filter-btn"
                      onClick={() => setWpFilterPopupOpen(false)}
                      style={{
                        background: 'black',
                        color: 'white',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Filter
                    </button>
                  </div>
                </div>
              )}

              <div className="table-wrapper">
                <table className="entities-table">
                  <thead>
                    <tr>
                      <th>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={wpSelectedIds.length === wpPageData.length && wpPageData.length > 0}
                            onChange={toggleAllWp}
                          />
                        </div>
                      </th>
                      <th><span>Name <ChevronsUpDown size={12} /></span></th>
                      <th><span>Status <ChevronsUpDown size={12} /></span></th>
                      <th><span>ID <ChevronsUpDown size={12} /></span></th>
                      <th><span>Workplace Type <ChevronsUpDown size={12} /></span></th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wpPageData.map((row, idx) => (
                      <tr key={row.workplace_id}
                        className={[
                          idx % 2 ? 'striped' : '',
                          (wpSelectedIds.includes(row.workplace_id) || selectedWorkspace?.workplace_id === row.workplace_id)
                            ? 'row-selected' : ''
                        ].join(' ')}
                        onClick={() => onWpRowClick(row)}
                      >
                        <td onClick={e => e.stopPropagation()}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={wpSelectedIds.includes(row.workplace_id)}
                              onChange={() => toggleSelectWp(row.workplace_id)}
                            />
                          </div>
                        </td>
                        <td>{row.workplace_name}</td>
                        <td><span className={`status ${row.status_text.toLowerCase()}`}>{row.status_text}</span></td>
                        <td>{row.workplace_id}</td>
                        <td>{row.location_type}</td>
                        <td style={{ textAlign: 'center', position: 'relative' }}>
                          <button
                            ref={el => wpButtonRefs.current[row.workplace_id] = el}
                            className="icon-btn"
                            onClick={e => { e.stopPropagation(); toggleWpDropdown(row.workplace_id); }}
                          >
                            <MoreHorizontal size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {wpDropdownOpenId !== null && (
                  <div
                    className="backdrop-cls"
                    style={{
                      position: 'absolute',
                      top: wpDropdownPosition.top,
                      left: wpDropdownPosition.left,
                      background: 'white',
                      boxShadow: '0px 1px 4px #0000001A',
                      zIndex: 1000,
                      minWidth: 152,
                      borderRadius: 8,
                      padding: 12
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="btn-align-forall">
                      <div className="common-for-all">
                        <button
                          onClick={() => {
                            onRowClick(wpCurrentRow);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                        >
                          View Details
                        </button>
                      </div>

                      <div className="common-for-all">
                        <button
                          onClick={() => {
                            history.push(`/company/edit-workplaces/${wpCurrentRow.workplace_id}`);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                        >
                          Edit
                        </button>
                      </div>

                      <div className="common-for-all">
                        <button
                          onClick={() => {
                            handleWpToggleStatus(wpCurrentRow);
                            setDropdownOpenId(null); // 👈 Close dropdown
                          }}
                          className="deactivate-cls"
                        >
                          {wpCurrentRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {filteredWorkplaces.length > 0 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(wpCurrentPage - 1) * PAGE_SIZE + 1} -
                    {Math.min(wpCurrentPage * PAGE_SIZE, filteredWorkplaces.length)} of {filteredWorkplaces.length} items
                  </div>
                  <div className="pagination-control">
                    <button
                      onClick={() => setWpCurrentPage(p => Math.max(1, p - 1))}
                      disabled={wpCurrentPage === 1}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    {[...Array(wpTotalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={wpCurrentPage === i + 1 ? 'active' : ''}
                        onClick={() => setWpCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setWpCurrentPage(p => Math.min(wpTotalPages, p + 1))}
                      disabled={wpCurrentPage === wpTotalPages}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {wpDrawerOpen && selectedWorkspace && (
        <>
          <div className="drawer-overlay" />
          <div className="entity-drawer" ref={wpDrawerRef}>
            <div className="drawer-header">
            <div className="d-flex align-items-center gap-1">
              {location.search.includes("from=branch") && (
                <button
                  className="back-btn"
                  onClick={() => {
                    const query = new URLSearchParams(location.search);
                    const branchId = query.get("branchId");

                    history.push(`/branches?id=${branchId}&autoOpen=true`);
                    setDrawerOpen(false);
                    setSelectedRow(null);
                    setHasAutoOpened(false);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                >
                  <ChevronLeft size={24} />
                </button>
              )}

              <h3>Workplace Info</h3>
              </div>
              <button
                className="close-btn"
                onClick={() => setWpDrawerOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="main-drawer-inner">
              <div className="drawer-title">
                <h2>{selectedWorkspace.workplace_name}</h2>
              </div>
              <div className="drawer-tabs">
                <button
                  className={wpDrawerTab === 'workplace-info' ? 'active' : ''}
                  onClick={() => setWpDrawerTab('workplace-info')}
                >
                  Details Info
                </button>
                <button
                  className={wpDrawerTab === 'additional-info' ? 'active' : ''}
                  onClick={() => setWpDrawerTab('additional-info')}
                >
                  Additional Info
                </button>
              </div>
              <div className="drawer-content">
                {wpDrawerTab === 'workplace-info' ? (
                  <>
                    <div className="tab-panel entity-info-panel">
                      <div className="field-block">
                        <div className="field-label">Name</div>
                        <div className="field-value">{selectedWorkspace.workplace_name}</div>
                      </div>
                      <div className="field-block">
                        <div className="field-label">Open ID</div>
                        <div className="field-value d-flex align-items-center gap-2">
                          <span
                            style={{ color: "#2C7BE5", cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => handleCopy(selectedWorkspace.uuid)}
                          >
                            {selectedWorkspace.uuid}
                          </span>
                          <Copy
                            size={13}
                            onClick={() => handleCopy(selectedWorkspace.uuid)}
                            style={{ cursor: "pointer", color: "#2C7BE5" }}
                          />
                          {copied && <span style={{ fontSize: '12px', color: "#2C7BE5" }}>Copied!</span>}
                        </div>
                      </div>
                      <div className="field-block">
                        <div className="field-label">Status</div>
                        <div className="field-value">
                          <span className={`status ${selectedWorkspace.status_text.toLowerCase()}`}>
                            {selectedWorkspace.status_text}
                          </span>
                        </div>
                      </div>
                      <div className="field-block">
                        <div className="field-label">Short Name</div>
                        <div className="field-value">{selectedWorkspace.workplace_short_name}</div>
                      </div>
                      <div className="field-block">
                        <div className="field-label">Location Type</div>
                        <div className="field-value">{selectedWorkspace.location_type}</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="tab-panel additional-info-panel">
                    <div className="field-block">
                      <div className="field-label">Note</div>
                      <div className="field-value notes-preserve">{selectedWorkspace.notes}</div>
                    </div>
                    {selectedWorkspace.location_type === 'Onsite' && (
                      <div>
                        <div className="field-block">
                          <div className="field-label">Address Line 1</div>
                          <div className="field-value">{selectedWorkspace.address.street_address  || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">Address Line 2</div>
                          <div className="field-value">{selectedWorkspace.address.street_address_2 || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">City</div>
                          <div className="field-value">{selectedWorkspace.address.city || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">State</div>
                          <div className="field-value">{selectedWorkspace.address.state || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">Post Code</div>
                          <div className="field-value">{selectedWorkspace.address.post_code || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">Country</div>
                          <div className="field-value">{selectedWorkspace.address.country || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                        <div className="field-block">
                          <div className="field-label">Google Link</div>
                          <div className="field-value">{selectedWorkspace.address.google_map_link || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Company;